import React, { useRef, useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Platform,
  Dimensions,
  Alert,
  Text
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { H1, H2, H3, Body1, Body2, Caption, ButtonText } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';
import { fetchSavingsGoals, fetchSavingsChallenges } from '../../api/pesa';
import { AuthContext } from '../../contexts/AuthContext';


const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const PesaScreen = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const { user } = React.useContext(AuthContext);
  const scrollY = useRef(new Animated.Value(0)).current;

  const [totalSavings, setTotalSavings] = useState(0);
  const [showSavings, setShowSavings] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [goals, challenges] = await Promise.all([
           fetchSavingsGoals(),
           fetchSavingsChallenges()
        ]);
        
        const goalsTotal = goals.reduce((sum, goal) => sum + parseFloat(goal.current_amount || 0), 0);
        const challengeTotal = challenges.reduce((sum, challenge) => {
           const p = challenge.participants?.find(p => p.user_id === user.id);
           return sum + parseFloat(p?.saved_amount || 0);
        }, 0);

        setTotalSavings(goalsTotal + challengeTotal);
      } catch (error) {
        console.log('Error loading pesa data:', error);
      }
    };


    // Refresh data when screen comes into focus
    const unsubscribe = navigation.addListener('focus', loadData);
    return unsubscribe;
  }, [navigation]);

  const features = [
    {
      icon: 'wallet-outline',
      title: 'Savings',
      description: 'Track and manage your savings goals',
      color: '#6366F1',
      route: 'Savings',
      comingSoon: true
    },
    {
      icon: 'calculator-outline',
      title: 'Budgets',
      description: 'Create and manage your budgets',
      color: '#10B981',
      route: 'Budgets'
    },
    {
      icon: 'trending-up-outline',
      title: 'Investments',
      description: 'Learn about investment opportunities',
      color: '#8B5CF6'
      // Linked to learning institutions later
    },
    {
      icon: 'book-outline',
      title: 'Edu-Finance',
      description: 'Access financial literacy resources',
      color: '#EC4899'
      // Linked to learning institutions later
    },
    {
      icon: 'cart-outline',
      title: 'Marketplace',
      description: 'Showcase and sell your products',
      color: '#F59E0B',
      route: 'Marketplace'
    },
    {
      icon: 'cash-outline',
      title: 'Loans',
      description: 'Check eligibility and apply for a loan',
      color: '#EC4899',
      route: 'Loans'
    }
  ];

  // Header Animation values
  const headerHeight = 200;

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 80],
    outputRange: [1, 0],
    extrapolate: 'clamp',
  });

  const stickyHeaderOpacity = scrollY.interpolate({
    inputRange: [80, 120],
    outputRange: [0, 1],
    extrapolate: 'clamp',
  });

  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      {/* Background Aura Section (Fixed) */}
      <View style={[styles.headerBackground, { height: headerHeight + 50 }]}>
        <LinearGradient
          colors={['#059669', '#10b981', '#34d399']}
          style={StyleSheet.absoluteFill}
        />
        <View style={styles.auraContainer}>
          <View style={[styles.aura, styles.aura1]} />
          <View style={[styles.aura, styles.aura2]} />
          <View style={[styles.aura, styles.aura3]} />
        </View>
      </View>

      {/* Mini Sticky Header */}
      <Animated.View
        style={[
          styles.miniStickyHeader,
          { paddingTop: insets.top + 5, opacity: stickyHeaderOpacity }
        ]}
        pointerEvents="box-none"
      >
        <LinearGradient
          colors={['#059669', '#10b981']}
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
        />
        <View style={styles.miniHeaderContent}>
          <H2 style={styles.miniHeaderTitle}>Financial Hub</H2>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.miniBackBtn}>
            <Ionicons name="chevron-back" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </Animated.View>

      <Animated.ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
        showsVerticalScrollIndicator={false}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: true }
        )}
        scrollEventThrottle={16}
      >
        {/* Full Hero Header */}
        <Animated.View style={[styles.heroHeader, { paddingTop: insets.top + 20, opacity: headerOpacity }]}>
          <View style={styles.topBar}>
            <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backCircle}>
              <Ionicons name="arrow-back" size={22} color="#FFFFFF" />
            </TouchableOpacity>
            <View style={styles.headerIcons}>
              <TouchableOpacity style={styles.iconCircle}>
                <Ionicons name="notifications-outline" size={20} color="#FFFFFF" />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.heroTitleBlock}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Caption style={styles.heroSubtitle}>PESA • EMPOWER</Caption>
              <View style={styles.overviewTag}>
                <Ionicons name="shield-checkmark" size={12} color="#FFFFFF" />
                <Caption style={styles.overviewTagText}>Secured in Treasury</Caption>
              </View>
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <H2 style={styles.heroTitle}>Manage Your Wealth</H2>
              <TouchableOpacity onPress={() => setShowSavings(!showSavings)}>
                <Ionicons name={showSavings ? "eye-outline" : "eye-off-outline"} size={22} color="#FFFFFF" />
              </TouchableOpacity>
            </View>
          </View>

          {/* Financial Overview Card (Moved to Top) */}
          <View style={styles.overviewCard}>
            <View style={styles.overviewRow}>
              <View>
                <Caption style={styles.overviewLabel}>Total Savings (Locked)</Caption>
                <H2 style={styles.overviewValue}>
                  Coming Soon
                </H2>
              </View>
              <View style={styles.overviewIcon}>
                <Ionicons name="lock-closed" size={20} color="#059669" />
              </View>
            </View>
          </View>



        </Animated.View>

        {/* Content Surface */}
        <View style={styles.contentSurface}>
          <H3 style={styles.surfaceTitle}>Quick Actions</H3>
          <Body2 style={styles.surfaceDesc}>
            Simple tools to help you track spending and grow your savings.
          </Body2>

          <View style={styles.featuresGrid}>
            {features.map((feature, index) => (
              <TouchableOpacity
                key={index}
                style={styles.featureCard}
                onPress={() => {
                  if (feature.comingSoon) {
                    Alert.alert("Coming Soon", "This feature is currently being upgraded and will be available soon.");
                    return;
                  }
                  if (feature.route) {
                    navigation.navigate(feature.route);
                  } else {
                    // Placeholder for unlinked features
                  }
                }}
              >
                <View style={[styles.iconContainer, { backgroundColor: feature.color + '15' }]}>
                  <Ionicons name={feature.icon} size={26} color={feature.color} />
                </View>
                <H3 style={styles.featureTitle}>{feature.title}</H3>
                <Caption style={styles.featureDescription}>{feature.description}</Caption>
                {feature.comingSoon && (
                  <View style={{ marginTop: 8, alignSelf: 'flex-start', backgroundColor: '#F3F4F6', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8 }}>
                    <Text style={{ fontSize: 10, fontWeight: '700', color: '#6B7280' }}>COMING SOON</Text>
                  </View>
                )}
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.footerSection}>
            <Caption style={styles.footerText}>Secure Financial Management • Youth Connect</Caption>
          </View>

        </View>
      </Animated.ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  headerBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 0,
  },
  auraContainer: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  aura: {
    position: 'absolute',
    borderRadius: 120,
    opacity: 0.2,
  },
  aura1: {
    width: 200,
    height: 200,
    top: -40,
    right: -20,
    backgroundColor: '#34D399',
  },
  aura2: {
    width: 160,
    height: 160,
    top: 40,
    left: -40,
    backgroundColor: '#10B981',
  },
  aura3: {
    width: 140,
    height: 140,
    bottom: -50,
    right: 30,
    backgroundColor: '#065F46',
  },
  miniStickyHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    height: 85,
    elevation: 4,
  },
  miniHeaderContent: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    height: 42,
    marginTop: 10,
  },
  miniHeaderTitle: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  miniBackBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollView: {
    flex: 1,
    zIndex: 1,
  },
  scrollViewContent: {
    paddingBottom: 0,
  },
  heroHeader: {
    paddingHorizontal: 18,
    paddingBottom: 35,
    zIndex: 2,
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  backCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.25)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroTitleBlock: {
    marginBottom: 16,
  },
  heroSubtitle: {
    color: 'rgba(255,255,255,0.8)',
    letterSpacing: 1.5,
    fontWeight: '800',
    fontSize: 10,
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: 22,
    fontWeight: '800',
    marginTop: 2,
  },
  overviewCard: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 16,
    padding: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  overviewRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  overviewLabel: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 12,
    fontWeight: '600'
  },
  overviewValue: {
    color: '#FFFFFF',
    fontSize: 26,
    fontWeight: '800',
    marginTop: 2
  },
  overviewIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center'
  },
  overviewFooter: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  overviewTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.2)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 10,
    gap: 6
  },
  overviewTagText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600'
  },
  contentSurface: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,


    paddingTop: 16,
    minHeight: SCREEN_HEIGHT - 80,
    zIndex: 5,
    marginTop: -40,
    paddingHorizontal: 18,
  },
  surfaceTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#111827',
    marginBottom: 10,
  },
  surfaceDesc: {
    color: '#6B7280',
    marginBottom: 18,
    fontSize: 13,
  },
  featuresGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  featureCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 14,
    marginBottom: 12,
    width: '48%',
    borderWidth: 1,
    borderColor: '#F3F4F6',
    ...getShadow('sm'),
  },
  iconContainer: {
    width: 42,
    height: 42,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  featureTitle: {
    fontSize: 13,
    fontWeight: '800',
    color: '#111827',
    marginBottom: 3,
  },
  featureDescription: {
    fontSize: 11,
    color: '#6B7280',
    lineHeight: 15,
  },
  footerSection: {
    alignItems: 'center',
    paddingVertical: 30,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    marginTop: 8,
  },
  footerText: {
    color: '#D1D5DB',
    fontSize: 10,
    marginBottom: 2
  },
  statsGridCompact: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: 15,
    marginTop: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
  },
  statItemCompact: {
    alignItems: 'center',
    flex: 1,
  },
  statLabelCompact: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 11,
    marginBottom: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statValueCompact: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
  },
  statDivider: {
    width: 1,
    height: '80%',
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignSelf: 'center',
  }
});

export default PesaScreen;
